import React, { useState } from 'react';
import './PageStyles.css';
import WaitlistModal from '../components/WaitlistModal';

const OurClients = () => {
    const [isWaitlistModalOpen, setIsWaitlistModalOpen] = useState(false);

    const testimonials = [
        {
            quote: "Colorkarma Directory transformed how we source suppliers. We found three new partners in our first week and cut our procurement time in half.",
            author: "Sarah Chen",
            role: "Procurement Manager",
            company: "Global Manufacturing Co.",
            type: "buyer"
        },
        {
            quote: "Listing on the platform has been a game-changer. We're getting qualified leads daily and have doubled our client base in just two months.",
            author: "Michael Rodriguez",
            role: "Sales Director",
            company: "Premium Materials Inc.",
            type: "supplier"
        },
        {
            quote: "The matching system is incredible. It connected us with suppliers we never would have found otherwise, and the quality is outstanding.",
            author: "Emily Johnson",
            role: "Operations Lead",
            company: "Innovation Labs",
            type: "buyer"
        },
        {
            quote: "Best decision we made this year. The platform handles all the heavy lifting of finding buyers, and we can focus on what we do best.",
            author: "David Kim",
            role: "Founder & CEO",
            company: "Artisan Solutions",
            type: "supplier"
        }
    ];

    const caseStudies = [
        {
            title: "Streamlining Supply Chain for Tech Startup",
            company: "TechStart Industries",
            type: "buyer",
            challenge: "Needed to find specialized component suppliers quickly for a product launch.",
            solution: "Used Colorkarma Directory to search and connect with 5 verified suppliers in 48 hours.",
            results: [
                "Reduced supplier search time by 75%",
                "Found suppliers with required certifications",
                "Launched product on schedule",
                "Established long-term partnerships"
            ],
            quote: "We went from searching to signing contracts in under a week. This platform saved us months of work."
        },
        {
            title: "Expanding Market Reach for Material Supplier",
            company: "Advanced Materials Group",
            type: "supplier",
            challenge: "Struggling to reach new markets and find qualified buyers for specialized products.",
            solution: "Listed products on Colorkarma Directory and optimized profile with certifications and capabilities.",
            results: [
                "50+ qualified leads in first month",
                "Expanded to 3 new geographic markets",
                "Increased sales pipeline by 200%",
                "Built relationships with enterprise buyers"
            ],
            quote: "The quality of leads is unmatched. Every inquiry is from a serious buyer looking for exactly what we offer."
        },
        {
            title: "Finding Sustainable Suppliers for Eco Brand",
            company: "GreenLife Products",
            type: "buyer",
            challenge: "Needed to find suppliers with specific sustainability certifications and ethical practices.",
            solution: "Used advanced filters to find suppliers matching sustainability criteria and verified certifications.",
            results: [
                "Found 8 certified sustainable suppliers",
                "Verified all certifications through platform",
                "Reduced carbon footprint of supply chain",
                "Met all sustainability goals"
            ],
            quote: "The certification filtering made it so easy to find partners aligned with our values. Game-changer for our mission."
        }
    ];

    return (
        <div className="page-container">
            {/* Hero Section */}
            <section className="page-hero">
                <div className="page-hero-content">
                    <h1 className="page-hero-title">Our Clients</h1>
                    <p className="page-hero-subtitle">
                        See how businesses like yours are using Colorkarma Directory to connect, grow, and succeed.
                    </p>
                </div>
            </section>

            {/* Testimonials Section */}
            <section className="testimonials-section">
                <div className="testimonials-container">
                    <h2 className="section-title">What Our Clients Say</h2>
                    <div className="testimonials-grid">
                        {testimonials.map((testimonial, index) => (
                            <div key={index} className="testimonial-card">
                                <div className="testimonial-quote">
                                    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" style={{ marginBottom: '1rem', opacity: 0.3 }}>
                                        <path d="M10 20C10 15 12 10 18 10C20 10 22 11 22 13C22 15 20 17 18 17C16 17 15 16 15 15C15 13 17 11 20 11C22 11 24 12 24 14C24 16 22 18 20 18C16 18 14 15 14 12C14 8 17 5 21 5C26 5 30 9 30 14C30 20 26 25 20 28V30H10V20Z" fill="currentColor" />
                                    </svg>
                                    <p className="testimonial-text">"{testimonial.quote}"</p>
                                </div>
                                <div className="testimonial-author">
                                    <div className="testimonial-author-info">
                                        <h4 className="testimonial-author-name">{testimonial.author}</h4>
                                        <p className="testimonial-author-role">{testimonial.role}</p>
                                        <p className="testimonial-author-company">{testimonial.company}</p>
                                    </div>
                                    <div className={`testimonial-badge ${testimonial.type}`}>
                                        {testimonial.type === 'buyer' ? 'Buyer' : 'Supplier'}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Case Studies Section */}
            <section className="case-studies-section">
                <div className="case-studies-container">
                    <h2 className="section-title">Success Stories</h2>
                    <p className="section-subtitle text-center mb-12 text-gray-600 text-lg">
                        Real results from real businesses using Colorkarma Directory
                    </p>

                    <div className="case-studies-list">
                        {caseStudies.map((study, index) => (
                            <div key={index} className="case-study-card">
                                <div className="case-study-header">
                                    <div>
                                        <div className={`case-study-badge ${study.type}`}>
                                            {study.type === 'buyer' ? 'Buyer Case Study' : 'Supplier Case Study'}
                                        </div>
                                        <h3 className="case-study-title">{study.title}</h3>
                                        <p className="case-study-company">{study.company}</p>
                                    </div>
                                </div>

                                <div className="case-study-content">
                                    <div className="case-study-section">
                                        <h4 className="case-study-section-title">The Challenge</h4>
                                        <p className="case-study-text">{study.challenge}</p>
                                    </div>

                                    <div className="case-study-section">
                                        <h4 className="case-study-section-title">The Solution</h4>
                                        <p className="case-study-text">{study.solution}</p>
                                    </div>

                                    <div className="case-study-section">
                                        <h4 className="case-study-section-title">The Results</h4>
                                        <ul className="case-study-results">
                                            {study.results.map((result, resultIndex) => (
                                                <li key={resultIndex} className="case-study-result-item">
                                                    <span className="case-study-checkmark">✓</span>
                                                    {result}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    <div className="case-study-quote">
                                        <p className="case-study-quote-text">"{study.quote}"</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="page-cta-section">
                <div className="page-cta-content">
                    <h2>Ready to Join Our Success Stories?</h2>
                    <p>Join our waitlist to get early access and start connecting with buyers and suppliers in your industry.</p>
                    <button className="btn-primary" onClick={() => setIsWaitlistModalOpen(true)}>Join the Waitlist</button>
                </div>
            </section>
            <WaitlistModal isOpen={isWaitlistModalOpen} onClose={() => setIsWaitlistModalOpen(false)} />
        </div>
    );
};

export default OurClients;

